import React, { useState } from 'react';
import type { HorseProfile } from '../../../types/horseProfile';
import { BasicInfoEditForm } from './forms/BasicInfoEditForm';
import { HealthEditForm } from './forms/HealthEditForm';
import { SocialBehaviorEditForm } from './forms/SocialBehaviorEditForm';
import { CareEditForm } from './forms/CareEditForm';
import { PerformanceEditForm } from './forms/PerformanceEditForm';
import { EmergencyEditForm } from './forms/EmergencyEditForm';

interface HorseProfileEditViewProps {
  horse: HorseProfile;
  section: keyof HorseProfile | null;
  onSave: (updatedProfile: Partial<HorseProfile>) => Promise<void>;
  onCancel: () => void;
}

export const HorseProfileEditView: React.FC<HorseProfileEditViewProps> = ({
  horse,
  section,
  onSave,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: any) => {
    setLoading(true);
    setError(null);

    try {
      if (!section) return;

      const updatedProfile: Partial<HorseProfile> = {
        ...horse,
        [section]: formData,
        lastUpdated: new Date().toISOString()
      };

      await onSave(updatedProfile);
    } catch (error) {
      console.error('Error saving section:', error);
      setError('Änderungen konnten nicht gespeichert werden');
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (section) {
      case 'health':
        return <HealthEditForm initialData={horse.health} onSubmit={handleSubmit} />;
      case 'socialBehavior':
        return <SocialBehaviorEditForm initialData={horse.socialBehavior} onSubmit={handleSubmit} />;
      case 'care':
        return <CareEditForm initialData={horse.care} onSubmit={handleSubmit} />;
      case 'performance':
        return <PerformanceEditForm initialData={horse.performance} onSubmit={handleSubmit} />;
      case 'emergency':
        return <EmergencyEditForm initialData={horse.emergency} onSubmit={handleSubmit} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {renderForm()}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => {
              const form = document.querySelector('form');
              if (form) {
                const submitEvent = new Event('submit', {
                  bubbles: true,
                  cancelable: true,
                });
                form.dispatchEvent(submitEvent);
              }
            }}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Wird gespeichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};